/**
 * Centralized process-flow fetching: single source of truth.
 * Only one in-flight request per store; results cached and dispatched so all consumers get data without duplicate API calls.
 * The actual API call is performed by a fetcher registered from utils/api/users.js (URL lives there).
 */
let fetcher = null

/**
 * Register the function that performs the process-flow get API call. Called from users.js.
 * @param { (store: string) => Promise<axios response> } fn
 */
export function registerFetcher(fn) {
  fetcher = typeof fn === 'function' ? fn : null
}

const cacheByStore = {}
const inFlightByStore = {}

const EVENT_REQUEST = 'processFlowRequest'
const EVENT_LOADED = 'processFlowLoaded'

function dispatchLoaded(store, response) {
  try {
    window.dispatchEvent(
      new CustomEvent(EVENT_LOADED, {
        detail: { store, response },
      })
    )
  } catch (e) {
    // ignore
  }
}

function handleProcessFlowRequest(e) {
  const store = typeof e?.detail?.store === 'string' ? e.detail.store.trim() : ''
  if (!store) return

  if (cacheByStore[store]) {
    dispatchLoaded(store, cacheByStore[store])
    return
  }
  if (inFlightByStore[store]) {
    return
  }
  if (!fetcher) {
    return
  }

  const promise = fetcher(store)
    .then((res) => {
      if (res?.data?.ok) {
        cacheByStore[store] = res
      }
      dispatchLoaded(store, res)
      return res
    })
    .catch((err) => {
      dispatchLoaded(store, null)
      throw err
    })
    .finally(() => {
      delete inFlightByStore[store]
    })

  inFlightByStore[store] = promise
}

if (typeof window !== 'undefined') {
  window.addEventListener(EVENT_REQUEST, handleProcessFlowRequest)
}

/**
 * Request process flow for a store. Dispatches processFlowRequest; returns a promise that resolves when processFlowLoaded fires for this store.
 * All callers share the same in-flight request and cache.
 * @param {string} store - Store identifier
 * @param {{ signal?: AbortSignal }} [options] - Optional. Pass { signal } to reject the promise when aborted.
 */
export function getProcessFlow(store, options = {}) {
  const s = typeof store === 'string' ? store.trim() : ''
  if (!s) return Promise.reject(new Error('store is required'))

  if (cacheByStore[s]) {
    return Promise.resolve(cacheByStore[s])
  }

  const signal = options?.signal

  return new Promise((resolve, reject) => {
    const cleanup = () => {
      window.removeEventListener(EVENT_LOADED, handler)
      if (signal) signal.removeEventListener('abort', onAbort)
    }
    const handler = (e) => {
      if (e.detail?.store !== s) return
      cleanup()
      const response = e.detail?.response
      if (response != null) {
        resolve(response)
      } else {
        reject(new Error('Process flow load failed'))
      }
    }
    const onAbort = () => {
      cleanup()
      reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'))
    }
    window.addEventListener(EVENT_LOADED, handler)
    if (signal?.aborted) {
      onAbort()
      return
    }
    if (signal) signal.addEventListener('abort', onAbort)
    window.dispatchEvent(new CustomEvent(EVENT_REQUEST, { detail: { store: s } }))
  })
}

/**
 * Get cached process flow payload for a store (no fetch, no dispatch).
 * Returns the .data payload (same shape as get_request_process_flow response.data).
 */
export function getCachedProcessFlow(store) {
  const s = typeof store === 'string' ? store.trim() : ''
  const cached = s ? cacheByStore[s] : null
  return cached?.data?.data ?? cached?.data ?? null
}

/**
 * Clear cache so next request will refetch. Call with no args to clear all, or pass a store to clear one.
 */
export function clearProcessFlowCache(store) {
  if (store != null && typeof store === 'string') {
    delete cacheByStore[store.trim()]
  } else {
    Object.keys(cacheByStore).forEach((k) => delete cacheByStore[k])
  }
}

export { EVENT_REQUEST, EVENT_LOADED }
